import { useState, useEffect } from 'react'
import { Trash2, Plus, GripVertical } from 'lucide-react'
import {
  useNavConfig,
  useUpdateNavConfig,
  useUpdateFooterConfig,
  DEFAULT_NAV_LINKS,
  DEFAULT_FOOTER_LINKS,
} from '@/hooks/use-nav-config'
import type { NavConfig, FooterConfig, NavLinkConfig } from '@/hooks/use-nav-config'

// ─── Predefined link IDs that cannot be deleted ────────────────────────────
const PREDEFINED_NAV_IDS = ['about', 'members', 'music', 'events', 'shop']
const PREDEFINED_FOOTER_IDS = ['about', 'members', 'music', 'events', 'shop', 'cheki']

// ─── Merge saved links with defaults (ensures new predefined links appear) ─
function mergeWithDefaults(
  saved: NavLinkConfig[] | undefined | null,
  defaults: NavLinkConfig[]
): NavLinkConfig[] {
  if (!saved || saved.length === 0) return defaults
  const savedIds = new Set(saved.map((l) => l.id))
  const missing = defaults.filter((d) => !savedIds.has(d.id))
  return [...saved, ...missing]
}

type Tab = 'navbar' | 'footer'

export default function NavigationSettingsPage() {
  const { data, isLoading } = useNavConfig()
  const updateNav = useUpdateNavConfig()
  const updateFooter = useUpdateFooterConfig()

  // ── Active tab ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<Tab>('navbar')

  // ── Navbar state ───────────────────────────────────────────────────────────
  const [navLinks, setNavLinks] = useState<NavLinkConfig[]>([])
  const [showCheki, setShowCheki] = useState(true)
  const [navSaved, setNavSaved] = useState(false)

  // ── Footer state ───────────────────────────────────────────────────────────
  const [footerLinks, setFooterLinks] = useState<NavLinkConfig[]>([])
  const [footerSaved, setFooterSaved] = useState(false)

  useEffect(() => {
    if (data) {
      setNavLinks(mergeWithDefaults(data.nav_config?.links, DEFAULT_NAV_LINKS))
      setShowCheki(data.nav_config?.show_cheki_button ?? true)
      setFooterLinks(mergeWithDefaults(data.footer_config?.links, DEFAULT_FOOTER_LINKS))
    }
  }, [data])

  // ── Nav helpers ────────────────────────────────────────────────────────────
  function updateNavLink(id: string, patch: Partial<NavLinkConfig>) {
    setNavLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }

  function addCustomNavLink() {
    const id = `custom-${Date.now()}`
    setNavLinks((prev) => [
      ...prev,
      { id, href: '', label: '', enabled: true, external: true },
    ])
  }

  function removeCustomNavLink(id: string) {
    setNavLinks((prev) => prev.filter((l) => l.id !== id))
  }

  function handleSaveNav() {
    const config: NavConfig = { links: navLinks, show_cheki_button: showCheki }
    updateNav.mutate(config, {
      onSuccess: () => {
        setNavSaved(true)
        setTimeout(() => setNavSaved(false), 3000)
      },
    })
  }

  // ── Footer helpers ─────────────────────────────────────────────────────────
  function updateFooterLink(id: string, patch: Partial<NavLinkConfig>) {
    setFooterLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }

  function handleSaveFooter() {
    const config: FooterConfig = { links: footerLinks }
    updateFooter.mutate(config, {
      onSuccess: () => {
        setFooterSaved(true)
        setTimeout(() => setFooterSaved(false), 3000)
      },
    })
  }

  if (isLoading) {
    return <p className="text-muted-foreground">Memuat konfigurasi navigasi...</p>
  }

  const predefinedNavLinks = navLinks.filter((l) => PREDEFINED_NAV_IDS.includes(l.id))
  const customNavLinks = navLinks.filter((l) => !PREDEFINED_NAV_IDS.includes(l.id))
  const predefinedFooterLinks = footerLinks.filter((l) => PREDEFINED_FOOTER_IDS.includes(l.id))

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Navigasi</h1>
        <p className="text-muted-foreground">
          Kustomisasi link yang tampil di navbar dan footer halaman publik.
        </p>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────────── */}
      <div className="flex border-b border-border">
        {(['navbar', 'footer'] as Tab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab === 'navbar' ? 'Navbar' : 'Footer'}
          </button>
        ))}
      </div>

      {/* ── Navbar Section ─────────────────────────────────────────────────── */}
      {activeTab === 'navbar' && (
      <section className="space-y-4">

        {/* Predefined links */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Link Halaman
          </h3>
          {predefinedNavLinks.map((link) => (
            <div key={link.id} className="flex items-center gap-3">
              <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                type="checkbox"
                checked={link.enabled}
                onChange={(e) => updateNavLink(link.id, { enabled: e.target.checked })}
                className="h-4 w-4 accent-primary shrink-0"
              />
              <input
                type="text"
                value={link.label}
                onChange={(e) => updateNavLink(link.id, { label: e.target.value })}
                maxLength={30}
                className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Label"
              />
              <span className="text-xs text-muted-foreground shrink-0 w-24">{link.href}</span>
            </div>
          ))}
        </div>

        {/* Custom external links */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Link Kustom (Eksternal)
          </h3>
          {customNavLinks.length === 0 && (
            <p className="text-sm text-muted-foreground italic">Belum ada link kustom.</p>
          )}
          {customNavLinks.map((link) => (
            <div key={link.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={link.enabled}
                onChange={(e) => updateNavLink(link.id, { enabled: e.target.checked })}
                className="h-4 w-4 accent-primary shrink-0"
              />
              <input
                type="text"
                value={link.label}
                onChange={(e) => updateNavLink(link.id, { label: e.target.value })}
                maxLength={30}
                className="w-28 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Label"
              />
              <input
                type="url"
                value={link.href}
                onChange={(e) => updateNavLink(link.id, { href: e.target.value })}
                className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="https://..."
              />
              <button
                type="button"
                onClick={() => removeCustomNavLink(link.id)}
                className="p-1.5 rounded-md text-destructive hover:bg-destructive/10 transition-colors"
                aria-label="Hapus link"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addCustomNavLink}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <Plus className="h-4 w-4" />
            Tambah Link Kustom
          </button>
        </div>

        {/* Cheki button toggle */}
        <div className="rounded-xl border border-border bg-card p-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={showCheki}
              onChange={(e) => setShowCheki(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            <span className="text-sm font-medium">Tampilkan tombol Cheki di navbar</span>
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveNav}
            disabled={updateNav.isPending}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateNav.isPending ? 'Menyimpan...' : 'Simpan Navbar'}
          </button>
          {navSaved && <p className="text-sm text-green-600">Navbar berhasil disimpan!</p>}
          {updateNav.error && (
            <p className="text-sm text-destructive">
              {(updateNav.error as Error).message ?? 'Terjadi kesalahan.'}
            </p>
          )}
        </div>
      </section>
      )}

      {/* ── Footer Section ─────────────────────────────────────────────────── */}
      {activeTab === 'footer' && (
      <section className="space-y-4">

        <div className="rounded-xl border border-border bg-card p-6 space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Link Halaman
          </h3>
          {predefinedFooterLinks.map((link) => (
            <div key={link.id} className="flex items-center gap-3">
              <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                type="checkbox"
                checked={link.enabled}
                onChange={(e) => updateFooterLink(link.id, { enabled: e.target.checked })}
                className="h-4 w-4 accent-primary shrink-0"
              />
              <input
                type="text"
                value={link.label}
                onChange={(e) => updateFooterLink(link.id, { label: e.target.value })}
                maxLength={30}
                className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Label"
              />
              <span className="text-xs text-muted-foreground shrink-0 w-24">{link.href}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveFooter}
            disabled={updateFooter.isPending}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateFooter.isPending ? 'Menyimpan...' : 'Simpan Footer'}
          </button>
          {footerSaved && <p className="text-sm text-green-600">Footer berhasil disimpan!</p>}
          {updateFooter.error && (
            <p className="text-sm text-destructive">
              {(updateFooter.error as Error).message ?? 'Terjadi kesalahan.'}
            </p>
          )}
        </div>
      </section>
      )}
    </div>
  )
}
